import { heroImages, images } from '../images';
import type { Project } from '../../types/content';

export const roadsAndWaterProjects: Project[] = [
  {
    id: 'a3-francistown-nata-section-1',
    title: 'A3 Francistown–Nata Road Reconstruction, Section 1',
    category: 'roads',
    location: 'Francistown – Mathangwane',
    client: 'Ministry of Transport and Infrastructure',
    parties: [
      { role: 'Project authority', name: 'Republic of Botswana, Ministry of Transport and Infrastructure' },
      { role: 'Financed by', name: 'Government of Botswana' },
      { role: 'Development manager', name: 'Okavango Africa Consortium' },
      { role: 'Subcontractor', name: 'UNIK/LIFTOFF/EVOLUTION Joint Venture' },
    ],
    relationship:
      'Liftoff Investments is a member of the UNIK/LIFTOFF/EVOLUTION Joint Venture, which is the subcontractor on this contract. Liftoff is not the main contractor.',
    summary:
      'Road construction works on Section 1 (Francistown–Mathangwane) of DM 2, the reconstruction of the A3 road from Francistown to Nata, documented as a 200 km route.',
    scope: [
      'Base and subbase skimming',
      'Processing of base layers on the bypass',
      'Processing of first fill and top of fill on the main road',
      'Soaking of base and subbase material ahead of processing',
      'Base compaction on the bypass',
      'Mixing of top of fill at the main road bus bay',
    ],
    period: 'Contract period: 30 months',
    reference: 'DM 2 – Reconstruction of Francistown–Nata Road',
    images: [
      { ...images.roadGrader, caption: 'Base and subbase skimming, CH 67+060 – 67+280' },
      { ...images.waterBowser, caption: 'Bypass: base and subbase soaking for processing, CH 66+400 – 67+080' },
      { ...heroImages.equipment, caption: 'Bypass: base compaction, CH 67+080 – CH 67+280' },
    ],
    featured: true,
    serviceIds: ['roadworks'],
    publication: 'published',
    reviewNotes: [
      'Replace the representative images with the seven road construction photographs from the A3 pull-up banner. The captions above are taken from the banner.',
      'The banner does not state contract dates or completion status, so none are shown.',
    ],
  },
  {
    id: 'francistown-kutamogoree-road',
    title: 'Francistown–Nata Road: Marapong to Kutamogoree Section',
    category: 'roads',
    location: 'Marapong – Kutamogoree',
    client: 'Ministry of Transport and Infrastructure',
    parties: [
      { role: 'Employer', name: 'Ministry of Transport and Infrastructure' },
      { role: 'Contact organisation', name: 'Okavango Africa Consortium' },
      { role: 'Joint venture', name: 'JV – UNIK/LIFTOFF' },
    ],
    relationship: 'Works carried out through the UNIK/LIFTOFF joint venture.',
    summary:
      'Roadbed construction and subbase and base materials construction for a 20 km section from Marapong to Kutamogoree, within the Francistown–Nata road reconstruction (Sections 1, 2 and 3).',
    scope: [
      'Roadbed construction',
      'Subbase materials construction',
      'Base materials construction',
      '20 km section from Marapong to Kutamogoree',
    ],
    status: 'Completion date listed: May 2027',
    value: 'P1.8 billion overall contract; Liftoff section P221 million',
    images: [{ ...images.roadGrader, caption: 'Representative image of roadbed and layer works' }],
    featured: true,
    serviceIds: ['roadworks'],
    publication: 'published',
    reviewNotes: ['Kept as a separate record from the A3 Section 1 banner project, as instructed. Confirm current status.'],
  },
  {
    id: 'kanye-mheelo-minimal-services',
    title: 'Kanye – Mheelo Ward Minimal Services',
    category: 'roads',
    location: 'Kanye, Mheelo ward',
    client: 'Ministry of Lands and Housing, Department of Technical Services',
    parties: [{ role: 'Client', name: 'Ministry of Lands and Housing, Department of Technical Services' }],
    summary:
      'Provision of minimal services, including potable water reticulation and G5 gravel road infrastructure with culverts and drifts.',
    scope: [
      '3.2 km of potable water reticulation',
      '30 km of 150 mm thick G5 gravel roads',
      'Culverts and drifts',
    ],
    status: 'Completed September 2016',
    value: 'P6,437,415.60',
    images: [{ ...images.culvert, caption: 'Representative image of culvert installation' }],
    serviceIds: ['roadworks', 'drainage', 'water'],
    publication: 'published',
  },
  {
    id: 'setlhoa-village-reticulation',
    title: 'Setlhoa Village Reticulation & Road Works',
    category: 'roads',
    location: 'Setlhoa Village, Block 10, Gaborone',
    client: 'Bothakga Burrow Botswana',
    parties: [{ role: 'Client', name: 'Bothakga Burrow Botswana' }],
    summary:
      'Sewer and water reticulation with 1.8 km of road works, including road surfacing and asphalt overlay, and stormwater concrete works.',
    scope: [
      'Sewer and water reticulation',
      '1.8 km of road works sections',
      'Road surfacing and asphalt overlay',
      'Stormwater concrete works',
    ],
    status: 'Completed August 2015',
    value: 'P3,200,000.00',
    images: [{ ...images.paving, caption: 'Representative image of completed road works' }],
    serviceIds: ['roadworks', 'water', 'drainage'],
    publication: 'published',
  },
  {
    id: 'airport-junction-mall',
    title: 'Airport Junction Mall Earthworks & Roads',
    category: 'roads',
    location: 'Airport Junction Mall, Gaborone',
    client: 'Stefanutti Stocks',
    parties: [{ role: 'Client', name: 'Stefanutti Stocks' }],
    summary: 'Construction of earthworks, road works and stormwater at Airport Junction Mall.',
    scope: ['Earthworks', 'Road works', 'Stormwater works'],
    status: 'Completed 30 November 2018',
    value: 'P1.2 million',
    images: [{ ...images.backhoe, caption: 'Representative image of earthworks' }],
    serviceIds: ['roadworks', 'civil', 'drainage'],
    publication: 'published',
  },
  {
    id: 'phakalane-roads',
    title: 'Phakalane Property Development Roads',
    category: 'roads',
    location: 'Phakalane, Gaborone North',
    client: 'Dangab Property Development (Pty) Ltd',
    parties: [{ role: 'Client', name: 'Dangab Property Development (Pty) Ltd' }],
    summary:
      'Infrastructure project on a 14-hectare plot: 3.2 km of roads built in two 150 mm gravel layers with double seal surfacing and stormwater.',
    scope: [
      '3.2 km of roads',
      'Two layers of 150 mm thick gravel',
      'Double seal surfacing',
      'Stormwater works',
    ],
    status: 'Completed August 2013',
    value: 'P1,600,000.00',
    images: [{ ...images.waterBowser, caption: 'Representative image of gravel layer works' }],
    serviceIds: ['roadworks', 'drainage'],
    publication: 'published',
  },
  {
    id: 'goodhope-vacuum-sewer',
    title: 'Refurbishment of Goodhope Vacuum Sewer System',
    category: 'water',
    location: 'Goodhope',
    client: 'Water Utilities Corporation',
    parties: [
      { role: 'Client', name: 'Water Utilities Corporation' },
      { role: 'Contractor', name: 'Liftoff Investments (Pty) Ltd' },
    ],
    summary:
      'Refurbishment of the Goodhope vacuum sewer infrastructure: vacuum sewer pump stations, the sewer reticulation pipeline, a lift station, vacuum sewer chambers, and electrical and telemetry works.',
    scope: [
      'Vacuum sewer pump stations (3 no.) and pumps',
      '65 km pipeline sewer reticulation',
      'Lift station',
      '250 no. vacuum sewer chambers',
      'Electrical and telemetry works (A&I)',
    ],
    reference: 'WUC. 014 [2024]',
    status: 'Completion date listed: April 2026',
    value: 'P15.4 million',
    components: [
      { label: 'Porta cabin at lift station', description: 'Site cabin installed at the lift station.' },
      { label: 'Vacuum vessel', description: 'Vacuum vessel with suction and discharge lines at the vacuum station.' },
      { label: 'Sewage discharge pumps', description: 'Pumps that discharge collected sewage from the vacuum vessel.' },
      { label: 'Pump lifting crane / gantry', description: 'Overhead gantry for lifting pumps, with its own electrical control panel.' },
      { label: 'Main control panel / MCC', description: 'Motor control centre panels for the station pumps.' },
      { label: 'Bush rotary vane vacuum pumps', description: 'Vacuum pumps that maintain vacuum in the collection network.' },
      { label: 'Valve chamber / sump cover', description: 'Outside collection chamber with a grated sump cover.' },
      { label: 'Bio-filter', description: 'Bio-filter installed for smell control.' },
      { label: 'Level transmitter kiosk', description: 'Kiosk housing the level transmitter.' },
      { label: 'Telemetry kiosk', description: 'Outdoor GRP kiosk housing the telemetry transmitter for remote level monitoring.' },
      { label: 'Chambers', description: 'Vacuum sewer collection chambers across the network.' },
    ],
    images: [
      { ...images.sewerStation, caption: 'Vacuum vessel, suction and discharge line at the vacuum station' },
      { ...images.pipework, caption: 'Sewage discharge pumps and pipework' },
    ],
    featured: true,
    serviceIds: ['water'],
    publication: 'published',
    reviewNotes: [
      'Replace the representative images with the 13 labelled photographs from the Goodhope pull-up banner.',
      'Component descriptions are paraphrased from the banner labels. Technical performance claims have been left out on purpose.',
    ],
  },
  {
    id: 'gaborone-wastewater-pump-stations',
    title: 'Gaborone Wastewater Pump Stations Refurbishment',
    category: 'water',
    location: 'Gaborone',
    client: 'Water Utilities Corporation',
    parties: [{ role: 'Employer', name: 'Water Utilities Corporation' }],
    summary: 'Refurbishment of Gaborone wastewater pump stations, electrical components and telemetry works.',
    scope: ['Wastewater pump station refurbishment', 'Electrical components', 'Telemetry works'],
    status: 'Listed as ongoing (October 2026 profile)',
    value: 'P18.5 million',
    images: [{ ...images.pipework, caption: 'Representative image of pump station pipework' }],
    serviceIds: ['water'],
    publication: 'published',
  },
  {
    id: 'orapa-water-reticulation',
    title: 'Orapa Mine Water Reticulation Upgrade',
    category: 'water',
    location: 'Orapa Mine Township and Green Area',
    client: 'Debswana Diamond Company',
    parties: [{ role: 'Employer', name: 'Debswana Diamond Company' }],
    summary:
      'Contract for an upgrade of the potable water reticulation system for Orapa Mine Township and the Green Area, including pump stations and electrical and telemetry works.',
    scope: [
      'Green Area: pump station with 7 no. pumps, electrical and telemetry works (A&I)',
      'Township area: pump house with 3 no. pumps, electrical and telemetry works',
      'Connection to existing infrastructure',
      'Elevated steel storage tanks on tower stands',
    ],
    status: 'Completed March 2021',
    value: 'P30.7 million',
    images: [
      { ...heroImages.water, caption: 'Representative image: elevated tanks being lifted onto tower stands' },
      { ...images.pipework, caption: 'Representative image: pump station pipework' },
    ],
    featured: true,
    serviceIds: ['water'],
    publication: 'published',
    reviewNotes: [
      'The profile cover page gives P30m and the project references give P30.7 million. Confirm which figure to publish.',
      'Replace with the Orapa photographs on page 2 of the profile.',
    ],
  },
  {
    id: 'serowe-sewer-effluent-irrigation',
    title: 'Serowe Sewer Effluent Irrigation Scheme',
    category: 'water',
    location: 'Serowe Village',
    client: 'Ministry of Agricultural Development & Food Security, Department of Crop Production',
    parties: [
      { role: 'Client', name: 'Ministry of Agricultural Development & Food Security, Department of Crop Production' },
      { role: 'Consultant', name: 'Pinagare Civil & Structural Engineers' },
    ],
    summary:
      'Harvesting of sewage effluent from existing treatment works into holding dams and distributing it to 41 ha of horticultural land parcels.',
    scope: [
      'Effluent holding dams / ponds and sludge lagoons with 3 mm HDPE lining',
      '3 no. ladies / gents ablution blocks',
      '1 no. pump station with 3 no. pumps for water distribution',
      '4 km water reticulation network',
      '250 m³ elevated distribution service reservoir',
      '4.2 km of road works with three layers of subbase and base course gravel',
    ],
    reference: 'Tender No. AGX 1/5/36 IV (78)',
    period: 'May 2016 – March 2017',
    status: 'Completed 30 April 2017',
    value: 'P16,623,547.95',
    images: [
      { ...images.pipework, caption: 'Representative image: irrigation pump house pipework' },
      { ...heroImages.water, caption: 'Representative image: elevated service reservoir' },
    ],
    featured: true,
    serviceIds: ['water', 'roadworks'],
    publication: 'published',
  },
  {
    id: 'matsiloje-steel-storage-tank',
    title: 'Matsiloje 500 m³ Steel Storage Tank',
    category: 'water',
    location: 'BDF Military Training Camp, Matsiloje Village',
    client: 'Water Utilities Corporation',
    parties: [{ role: 'Employer', name: 'Water Utilities Corporation' }],
    summary: 'Design and build, supply and installation of a 500 m³ galvanised steel storage tank on a 15 m stand.',
    scope: ['Design and build', 'Supply and installation of 500 m³ galvanised steel tank', '15 m tank stand'],
    status: 'Completed October 2017',
    value: 'P3.46 million',
    images: [{ ...heroImages.water, caption: 'Representative image of an elevated steel tank installation' }],
    serviceIds: ['water'],
    publication: 'published',
  },
];
